import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import inventoryItemService from '../../services/inventoryItemService';
import venueSupplierService from '../../services/venueSupplierService';
import venueLocationService, { pickDefaultLocation } from '../../services/venueLocationService';
import quickReceiveService from '../../services/quickReceiveService';
import ownershipAccountService from '../../services/ownershipAccountService';
import { getPrefill } from '../../services/supplierItemLinkService';
import type { InventoryItemWithBalance } from '../../services/inventoryItemService';
import type { OwnershipAccount, Supplier, VenueLocation } from '../../types/venue';
import type { QuickReceiveResult } from '../../services/quickReceiveService';

// ── Types ─────────────────────────────────────────────────────────────────────

interface QRRow {
  id: string;
  item: InventoryItemWithBalance | null;
  supplierId: string;
  qty: string;
  unitCost: string;
  supplierTouched: boolean;
  costTouched: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  venueId: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

let rowCounter = 0;
const newRowId = () => `row-${++rowCounter}`;

const buildDisplayName = (item: InventoryItemWithBalance): string => item.displayName;

const formatItemSize = (item: InventoryItemWithBalance): string => {
  if (item.sizeValue != null) return `${item.sizeValue} ${item.sizeUnit ?? 'ml'}`;
  return '';
};

const todayISO = () => new Date().toISOString().split('T')[0];

const emptyRow = (): QRRow => ({
  id: newRowId(),
  item: null,
  supplierId: '',
  qty: '1',
  unitCost: '',
  supplierTouched: false,
  costTouched: false,
});

// ── Component ─────────────────────────────────────────────────────────────────

const QuickReceiveModal: React.FC<Props> = ({ open, onClose, venueId, onSuccess }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  // ── Header fields ─────────────────────────────────────────────────────────────
  const [receivedDate, setReceivedDate] = useState(todayISO());
  const [receivedBy, setReceivedBy] = useState('');
  const [headerNotes, setHeaderNotes] = useState('');
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // ── Rows ──────────────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<QRRow[]>([emptyRow()]);

  // ── Per-row item search state (keyed by rowId) ────────────────────────────────
  const [rowSearchResults, setRowSearchResults] = useState<Record<string, InventoryItemWithBalance[]>>({});
  const [rowSearchLoading, setRowSearchLoading] = useState<Record<string, boolean>>({});
  const searchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // ── Suppliers ─────────────────────────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  // ── Ownership ─────────────────────────────────────────────────────────────────
  const [ownershipAccountId, setOwnershipAccountId] = useState('');
  const [ownershipAccounts, setOwnershipAccounts] = useState<OwnershipAccount[]>([]);

  // ── Submission ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<QuickReceiveResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // ── Reset on open ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setReceivedDate(todayISO());
      setReceivedBy(user?.email ?? user?.firstName ?? '');
      setHeaderNotes('');
      setLocationId('');
      setOwnershipAccountId('');
      setRows([emptyRow()]);
      setRowSearchResults({});
      setRowSearchLoading({});
      setResult(null);
      setSubmitError(null);
      setTouched(false);
    }
  }, [open, user]);

  // ── Load locations on open ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !venueId) return;
    setLocationsLoading(true);
    venueLocationService.getLocations(venueId)
      .then(locs => {
        const active = locs.filter(l => l.active);
        setLocations(active);
        const def = pickDefaultLocation(active);
        if (def) setLocationId(def._id);
      })
      .catch(() => setLocations([]))
      .finally(() => setLocationsLoading(false));
  }, [open, venueId]);

  // ── Load suppliers once on open ───────────────────────────────────────────────
  // Venue-scoped — only suppliers connected to this venue.
  useEffect(() => {
    if (!open || !venueId) return;
    venueSupplierService.listVenueSuppliers(venueId, 1, 500)
      .then(r => setSuppliers(r.data ?? []))
      .catch(() => { /* non-blocking */ });
  }, [open, venueId]);

  // ── Load ownership accounts and pre-select venue default ──────────────────────
  useEffect(() => {
    if (!open || !venueId) return;
    ownershipAccountService.listAccounts(venueId, { active: true })
      .then(res => {
        setOwnershipAccounts(res.data);
        const def = res.data.find(a => a.isVenueDefault);
        if (def) setOwnershipAccountId(def._id);
      })
      .catch(() => { /* non-blocking — backend resolves default if omitted */ });
  }, [open, venueId]);

  // ── Row search ────────────────────────────────────────────────────────────────
  const handleItemSearch = (rowId: string, inputValue: string) => {
    // Clear previous timer for this row
    if (searchTimers.current[rowId]) clearTimeout(searchTimers.current[rowId]);

    if (!inputValue.trim()) {
      setRowSearchResults(prev => ({ ...prev, [rowId]: [] }));
      setRowSearchLoading(prev => ({ ...prev, [rowId]: false }));
      return;
    }

    setRowSearchLoading(prev => ({ ...prev, [rowId]: true }));

    searchTimers.current[rowId] = setTimeout(async () => {
      try {
        const res = await inventoryItemService.getItems(venueId, 1, 20, 'displayName', 'asc', inputValue);
        setRowSearchResults(prev => ({ ...prev, [rowId]: res.data ?? [] }));
      } catch {
        setRowSearchResults(prev => ({ ...prev, [rowId]: [] }));
      } finally {
        setRowSearchLoading(prev => ({ ...prev, [rowId]: false }));
      }
    }, 300);
  };

  // ── Row mutations ─────────────────────────────────────────────────────────────
  const updateRow = (id: string, changes: Partial<QRRow>) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...changes } : r));
  };

  const handleItemSelect = (rowId: string, item: InventoryItemWithBalance | null) => {
    const balanceFallback = item?.balance?.lastCost != null ? String(item.balance.lastCost) : '';
    updateRow(rowId, {
      item,
      unitCost: balanceFallback,
      supplierId: '',
      supplierTouched: false,
      costTouched: false,
    });
    setRowSearchResults(prev => ({ ...prev, [rowId]: [] }));

    if (!item) return;

    // Capture item id for stale-response guard
    const selectedItemId = item._id;

    getPrefill(venueId, item._id).then(result => {
      setRows(prev => {
        const row = prev.find(r => r.id === rowId);
        // Ignore if row no longer exists or item has changed
        if (!row || row.item?._id !== selectedItemId) return prev;
        const updates: Partial<QRRow> = {};
        if (result.suggestedSupplierId && !row.supplierTouched) {
          updates.supplierId = result.suggestedSupplierId;
        }
        if (result.suggestedCost !== null && !row.costTouched) {
          updates.unitCost = String(result.suggestedCost);
        }
        if (Object.keys(updates).length === 0) return prev;
        return prev.map(r => r.id === rowId ? { ...r, ...updates } : r);
      });
    }).catch(() => { /* non-blocking */ });
  };

  const handleSupplierChange = (rowId: string, newSupplierId: string) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, supplierId: newSupplierId, supplierTouched: true } : r));

    const row = rows.find(r => r.id === rowId);
    if (!row?.item || !newSupplierId) return;

    const selectedItemId = row.item._id;

    getPrefill(venueId, row.item._id, newSupplierId).then(result => {
      setRows(prev => {
        const current = prev.find(r => r.id === rowId);
        if (!current || current.item?._id !== selectedItemId) return prev;
        if (result.suggestedCost !== null && !current.costTouched) {
          return prev.map(r => r.id === rowId ? { ...r, unitCost: String(result.suggestedCost) } : r);
        }
        return prev;
      });
    }).catch(() => { /* non-blocking */ });
  };

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const removeRow = (id: string) => {
    setRows(prev => prev.length === 1 ? [emptyRow()] : prev.filter(r => r.id !== id));
    // Cleanup search state
    setRowSearchResults(prev => { const n = { ...prev }; delete n[id]; return n; });
    setRowSearchLoading(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  // ── Validation ────────────────────────────────────────────────────────────────
  const rowErrors = useMemo(() => {
    if (!touched) return {};
    const errors: Record<string, string[]> = {};

    rows.forEach(row => {
      const rowErrs: string[] = [];
      if (!row.item) rowErrs.push('Select an inventory item');
      if (!row.supplierId) rowErrs.push('Select a supplier');
      const qty = parseInt(row.qty, 10);
      if (isNaN(qty) || qty < 1) rowErrs.push('Qty must be ≥ 1');
      if (row.unitCost !== '' && (isNaN(parseFloat(row.unitCost)) || parseFloat(row.unitCost) < 0)) {
        rowErrs.push('Unit cost must be a positive number');
      }
      if (rowErrs.length > 0) errors[row.id] = rowErrs;
    });

    // Check duplicate item+supplier combos
    const seen = new Set<string>();
    rows.forEach(row => {
      if (!row.item || !row.supplierId) return;
      const key = `${row.item._id}::${row.supplierId}`;
      if (seen.has(key)) {
        errors[row.id] = [...(errors[row.id] ?? []), 'Duplicate item + supplier combination'];
      }
      seen.add(key);
    });

    return errors;
  }, [rows, touched]);

  const hasErrors = Object.keys(rowErrors).length > 0 || !receivedBy.trim() || !locationId;
  const activeRows = rows.filter(r => r.item && r.supplierId && parseInt(r.qty, 10) >= 1);

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setTouched(true);
    if (hasErrors || activeRows.length === 0) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);

    try {
      const res = await quickReceiveService.submit(venueId, {
        locationId,
        receivedDate,
        receivedBy: receivedBy.trim(),
        notes: headerNotes.trim() || undefined,
        ...(ownershipAccountId && { ownershipAccountId }),
        lines: activeRows.map(row => ({
          inventoryItemId: row.item!._id,
          supplierId: row.supplierId,
          receivedQty: parseInt(row.qty, 10),
          ...(row.unitCost !== '' && { unitCost: parseFloat(row.unitCost) }),
        })),
      });

      setResult(res);

      // If fully successful, notify parent to reload inventory
      if (res.failed.length === 0) {
        onSuccess();
      }
    } catch (err: any) {
      // 422 when all groups fail — the body has a `failed` array, not a top-level message
      const failedGroups: Array<{ supplierId: string; error: string }> | undefined =
        err?.response?.data?.failed;
      if (failedGroups && failedGroups.length > 0) {
        setSubmitError(failedGroups.map(f => f.error).join(' · '));
      } else {
        setSubmitError(err?.response?.data?.message ?? err?.message ?? 'Quick receive failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Supplier name lookup ──────────────────────────────────────────────────────
  const supplierName = (id: string) => suppliers.find(s => s._id === id)?.name ?? id;

  // ── Render: Success state ─────────────────────────────────────────────────────
  if (result && result.failed.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle sx={{ fontWeight: 700 }}>Quick Receive</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
            <SuccessIcon sx={{ fontSize: 52, color: '#1a7f37' }} />
            <Typography variant="h6" fontWeight={700}>Stock Updated</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {result.totalMovementsCreated} movement{result.totalMovementsCreated !== 1 ? 's' : ''} posted
              across {result.results.length} supplier{result.results.length !== 1 ? 's' : ''}.
            </Typography>
            <Box sx={{ width: '100%', mt: 1 }}>
              {result.results.map(r => (
                <Box
                  key={r.purchaseOrderId}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 0.75,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Typography variant="body2">{supplierName(r.supplierId)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.linesPosted} line{r.linesPosted !== 1 ? 's' : ''} · {r.movementsCreated} movement{r.movementsCreated !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Render: Partial failure / form ────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          Quick Receive
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, fontWeight: 400 }}>
            Record a delivery without a formal purchase order
          </Typography>
        </Box>
        {!submitting && (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important' }}>
        {/* Partial failure result */}
        {result && result.failed.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {result.results.length > 0 && (
              <Alert severity="success">
                {result.results.length} supplier group{result.results.length !== 1 ? 's' : ''} posted successfully
                ({result.totalMovementsCreated} movements). Inventory has been updated for these.
              </Alert>
            )}
            {result.failed.map(f => (
              <Alert key={f.supplierId} severity="error">
                <strong>{supplierName(f.supplierId)}:</strong> {f.error}
              </Alert>
            ))}
          </Box>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {/* Header fields */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
          <TextField
            label="Received Date"
            type="date"
            value={receivedDate}
            onChange={e => setReceivedDate(e.target.value)}
            size="small"
            sx={{ width: 170 }}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={submitting}
          />
          <TextField
            label="Received By *"
            value={receivedBy}
            onChange={e => setReceivedBy(e.target.value)}
            size="small"
            sx={{ width: 220 }}
            error={touched && !receivedBy.trim()}
            helperText={touched && !receivedBy.trim() ? 'Required' : ''}
            disabled={submitting}
          />
          <TextField
            select
            label="Receiving Location *"
            value={locationId}
            onChange={e => setLocationId(e.target.value)}
            size="small"
            sx={{ width: 200 }}
            error={touched && !locationId}
            helperText={touched && !locationId ? 'Required' : ''}
            disabled={submitting || locationsLoading}
            slotProps={{
              input: {
                endAdornment: locationsLoading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null,
              },
            }}
          >
            <MenuItem value="" disabled>
              <Typography variant="body2" color="text.disabled">Select location…</Typography>
            </MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Notes"
            value={headerNotes}
            onChange={e => setHeaderNotes(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            placeholder="Optional"
            disabled={submitting}
          />
          {ownershipAccounts.length > 0 && (
            <TextField
              select
              label="Ownership / Consignment"
              value={ownershipAccountId}
              onChange={e => setOwnershipAccountId(e.target.value)}
              size="small"
              sx={{ width: 220 }}
              helperText="Leave as default unless consigned"
              disabled={submitting}
            >
              <MenuItem value="">Use venue default</MenuItem>
              {ownershipAccounts.map(acc => (
                <MenuItem key={acc._id} value={acc._id}>
                  {acc.name}{acc.isVenueDefault ? ' (default)' : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Lines table */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Item', 'Supplier', 'Qty', 'Unit Cost', ''].map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      py: 1.25,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const errs = rowErrors[row.id] ?? [];
                const hasRowError = errs.length > 0;
                return (
                  <TableRow
                    key={row.id}
                    sx={{ verticalAlign: 'top', backgroundColor: hasRowError ? alpha(theme.palette.error.main, 0.03) : undefined }}
                  >
                    {/* Item autocomplete */}
                    <TableCell sx={{ minWidth: 280, py: 1 }}>
                      <Autocomplete
                        size="small"
                        options={rowSearchResults[row.id] ?? []}
                        value={row.item}
                        loading={rowSearchLoading[row.id] ?? false}
                        filterOptions={x => x}
                        getOptionLabel={buildDisplayName}
                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') handleItemSearch(row.id, value);
                          if (reason === 'clear') updateRow(row.id, { item: null, unitCost: '' });
                        }}
                        onChange={(_, value) => handleItemSelect(row.id, value)}
                        disabled={submitting}
                        renderOption={(props, opt) => (
                          <Box component="li" {...props} key={opt._id}>
                            <Typography variant="body2" fontWeight={500}>
                              {buildDisplayName(opt)}
                            </Typography>
                          </Box>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            placeholder="Search item…"
                            error={touched && !row.item}
                            slotProps={{
                              input: {
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {rowSearchLoading[row.id] && <CircularProgress size={14} />}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              },
                            }}
                          />
                        )}
                      />
                    </TableCell>

                    {/* Supplier select */}
                    <TableCell sx={{ minWidth: 180, py: 1 }}>
                      <Autocomplete
                        size="small"
                        options={suppliers}
                        value={suppliers.find(s => s._id === row.supplierId) ?? null}
                        getOptionLabel={s => s.name}
                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                        onChange={(_, s) => handleSupplierChange(row.id, s?._id ?? '')}
                        disabled={submitting}
                        renderInput={params => (
                          <TextField
                            {...params}
                            placeholder="Select supplier…"
                            error={touched && !row.supplierId}
                          />
                        )}
                      />
                    </TableCell>

                    {/* Qty */}
                    <TableCell sx={{ width: 90, py: 1 }}>
                      <TextField
                        value={row.qty}
                        onChange={e => updateRow(row.id, { qty: e.target.value })}
                        type="number"
                        size="small"
                        sx={{ width: 80 }}
                        slotProps={{ htmlInput: { min: 1, step: 1 } }}
                        error={touched && (isNaN(parseInt(row.qty, 10)) || parseInt(row.qty, 10) < 1)}
                        disabled={submitting}
                      />
                    </TableCell>

                    {/* Unit cost */}
                    <TableCell sx={{ width: 110, py: 1 }}>
                      <TextField
                        value={row.unitCost}
                        onChange={e => updateRow(row.id, { unitCost: e.target.value, costTouched: true })}
                        type="number"
                        size="small"
                        sx={{ width: 96 }}
                        placeholder="—"
                        slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                        disabled={submitting}
                      />
                    </TableCell>

                    {/* Remove */}
                    <TableCell sx={{ width: 40, py: 1 }}>
                      <Tooltip title="Remove row">
                        <IconButton
                          size="small"
                          onClick={() => removeRow(row.id)}
                          disabled={submitting}
                          sx={{ color: theme.palette.text.disabled }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Inline errors summary */}
        {touched && Object.keys(rowErrors).length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            {Object.entries(rowErrors).map(([rowId, errs]) => {
              const rowIdx = rows.findIndex(r => r.id === rowId);
              return (
                <Typography key={rowId} variant="caption" color="error" sx={{ display: 'block' }}>
                  Row {rowIdx + 1}: {errs.join(', ')}
                </Typography>
              );
            })}
          </Box>
        )}

        {touched && activeRows.length === 0 && Object.keys(rowErrors).length === 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Add at least one complete line (item + supplier + qty) before submitting.
          </Alert>
        )}

        {/* Add row */}
        <Box sx={{ mt: 1.5 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={addRow}
            disabled={submitting}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Add row
          </Button>
        </Box>

        {/* Summary */}
        {activeRows.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {activeRows.length} line{activeRows.length !== 1 ? 's' : ''} ready ·{' '}
            {new Set(activeRows.map(r => r.supplierId).filter(Boolean)).size} supplier{new Set(activeRows.map(r => r.supplierId).filter(Boolean)).size !== 1 ? 's' : ''}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || activeRows.length === 0}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {submitting ? 'Posting…' : `Post ${activeRows.length > 0 ? `${activeRows.length} line${activeRows.length !== 1 ? 's' : ''}` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickReceiveModal;
